import type { BookSource, ContentKind, OpenChapterResult, UserSpace } from "../api";

interface ReaderSessionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ReaderSessionTarget {
  bookId: string;
  title: string;
  kind: ContentKind;
  sourceId: string;
  sourceBookId: string;
  chapterId?: string;
  chapterIndex?: number;
  paragraphIndex?: number;
  scrollRatio?: number;
}

export interface ReaderContentSnapshot {
  userId: string;
  user?: UserSpace;
  target: ReaderSessionTarget;
  opened: OpenChapterResult;
  openedChapters: OpenChapterResult["chapter"][];
  pendingShelfConfirmation?: boolean;
}

export const readerSessionKey = "reader:active-target";
export const readerLastTargetKey = "reader:last-target";
export const readerContentSnapshotKey = "reader:content-snapshot";

export function parseReaderSessionTarget(raw: string | null): ReaderSessionTarget | null {
  if (!raw) return null;

  try {
    return JSON.parse(raw) as ReaderSessionTarget;
  } catch {
    return null;
  }
}

export function parseReaderContentSnapshot(raw: string | null): ReaderContentSnapshot | null {
  if (!raw) return null;

  try {
    const snapshot = JSON.parse(raw) as ReaderContentSnapshot;
    if (!snapshot.userId || !snapshot.target || !snapshot.opened?.chapter?.chapterId) return null;
    return snapshot;
  } catch {
    return null;
  }
}

export function serializeReaderSessionTarget(target: ReaderSessionTarget): string {
  return JSON.stringify(target);
}

export function readActiveReaderSession(storage: ReaderSessionStorage): ReaderSessionTarget | null {
  const raw = storage.getItem(readerSessionKey);
  const target = parseReaderSessionTarget(raw);

  if (!target && raw) {
    storage.removeItem(readerSessionKey);
  }

  return target;
}

export function readReaderContentSnapshot(storage: ReaderSessionStorage): ReaderContentSnapshot | null {
  const raw = storage.getItem(readerContentSnapshotKey);
  const snapshot = parseReaderContentSnapshot(raw);

  if (!snapshot && raw) {
    storage.removeItem(readerContentSnapshotKey);
  }

  return snapshot;
}

export function readStoredReaderTarget(
  sessionStorage: ReaderSessionStorage,
  localStorage: ReaderSessionStorage,
): ReaderSessionTarget | null {
  const target = readActiveReaderSession(sessionStorage);
  if (target) return target;

  const raw = localStorage.getItem(readerLastTargetKey);
  const fallbackTarget = parseReaderSessionTarget(raw);

  if (!fallbackTarget && raw) {
    localStorage.removeItem(readerLastTargetKey);
  }

  return fallbackTarget;
}

export function readStoredReaderContentSnapshot(
  sessionStorage: ReaderSessionStorage,
  localStorage: ReaderSessionStorage,
): ReaderContentSnapshot | null {
  return readReaderContentSnapshot(sessionStorage) ?? readReaderContentSnapshot(localStorage);
}

export function writeReaderSessionTarget(
  target: ReaderSessionTarget,
  sessionStorage: ReaderSessionStorage,
  localStorage: ReaderSessionStorage,
) {
  const payload = serializeReaderSessionTarget(target);
  safeSetItem(sessionStorage, readerSessionKey, payload);
  safeSetItem(localStorage, readerLastTargetKey, payload);
}

export function writeReaderContentSnapshot(
  snapshot: ReaderContentSnapshot,
  storage: ReaderSessionStorage,
  fallbackStorage?: ReaderSessionStorage,
) {
  const payload = JSON.stringify(snapshot);

  safeSetItem(storage, readerContentSnapshotKey, payload);
  if (fallbackStorage) safeSetItem(fallbackStorage, readerContentSnapshotKey, payload);
}

export function clearReaderSessionStorage(sessionStorage: ReaderSessionStorage, localStorage: ReaderSessionStorage) {
  sessionStorage.removeItem(readerSessionKey);
  sessionStorage.removeItem(readerContentSnapshotKey);
  localStorage.removeItem(readerLastTargetKey);
  localStorage.removeItem(readerContentSnapshotKey);
}

export function isReaderContentSnapshotForTarget(snapshot: ReaderContentSnapshot | null, userId: string, target: ReaderSessionTarget): snapshot is ReaderContentSnapshot {
  if (!snapshot) return false;

  return (
    snapshot.userId === userId &&
    snapshot.target.bookId === target.bookId &&
    snapshot.target.sourceId === target.sourceId &&
    snapshot.target.sourceBookId === target.sourceBookId &&
    snapshot.target.chapterId === target.chapterId
  );
}

export function createFallbackBookSource(target: ReaderSessionTarget): BookSource {
  return {
    id: `${target.bookId}:${target.sourceId}`,
    bookId: target.bookId,
    sourceId: target.sourceId,
    sourceBookId: target.sourceBookId,
  };
}

function safeSetItem(storage: ReaderSessionStorage, key: string, value: string) {
  try {
    storage.setItem(key, value);
  } catch {
    // 浏览器存储额度不足时不能阻断阅读流程
  }
}
